/**
 * Finding the thing a tour step points at, and measuring it.
 *
 * ── WHY THIS IS A HOOK OF ITS OWN ──────────────────────────────────────────
 *
 * Everything that can go wrong with a product tour goes wrong here. The step
 * list is static; the DOM is not. A step's anchor may be:
 *
 *   · absent for ever — the user's account does not have that section
 *   · absent for now — the route is still loading, or the query behind the
 *     sidebar badge has not resolved
 *   · present but moving — a banner appeared above it, or the page scrolled
 *   · present but off-screen — below the fold in a long sidebar
 *
 * The first two look identical at the instant you ask. Treating "not there
 * yet" as "not there" makes the tour skip steps on a slow connection; treating
 * "not there" as "not there yet" hangs the tour on a spinner. So: ask, then
 * watch for a bounded period, then give up and let the caller skip the step.
 *
 * ── WHY NOT A REF ──────────────────────────────────────────────────────────
 *
 * The obvious React answer is for each anchor to hand the tour a ref. That
 * would mean every one of them importing the tour, and the sidebar knowing
 * which of its forty entries the tour happens to use this month. A
 * `data-tutorial` attribute and a `querySelector` keeps the dependency one way:
 * the anchors do not know the tour exists.
 */

import { useEffect, useState } from 'react';
import { ANCHOR_TIMEOUT_MS, type TutorialStep } from './tutorialSteps';

/** A measured rectangle, in viewport coordinates. */
export interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type AnchorOutcome =
  /** Waiting — asked, not found, still inside the timeout. */
  | { state: 'pending' }
  /** Found and measured. */
  | { state: 'found'; rect: AnchorRect; element: HTMLElement }
  /**
   * The step names no anchor: its card belongs in the middle of the screen.
   *
   * SEPARATE FROM `absent` ON PURPOSE. Both mean "there is no rectangle", and
   * the first version of this hook returned `absent` for both — which made the
   * controller skip the welcome and the closing step, because skipping is what
   * `absent` means. Two conditions that produce the same rectangle are still
   * two conditions.
   */
  | { state: 'centred' }
  /** Named an anchor, and it is not on this screen. The caller skips the step. */
  | { state: 'absent' };

/**
 * The anchors a step will accept, in order of preference.
 *
 * Exported because the "is the tour coherent for this role" test walks it — the
 * one thing worth asserting about the step list is that every step either has
 * somewhere to point or is deliberately centred.
 */
export function anchorsFor(step: TutorialStep): readonly string[] {
  if (step.anchor) return [step.anchor];
  if (step.anchorAny) return step.anchorAny;
  return [];
}

/** First anchor in the step's preference list that is actually in the DOM. */
export function findAnchorElement(step: TutorialStep): HTMLElement | null {
  for (const name of anchorsFor(step)) {
    const el = document.querySelector<HTMLElement>(`[data-tutorial="${name}"]`);
    if (el) return el;
  }
  return null;
}

function measure(el: HTMLElement): AnchorRect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * Resolve and keep measuring one step's anchor.
 *
 * Re-measures on scroll and resize, and on any DOM mutation while still
 * waiting. Once found, the MutationObserver is dropped — leaving one attached
 * to the whole body for the duration of the tour would fire on every render of
 * every page the tour walks through, and the scroll and resize listeners
 * already cover the cases that move a resolved anchor.
 */
export function useTutorialAnchor(step: TutorialStep | null): AnchorOutcome {
  /*
   * THE OUTCOME CARRIES THE STEP IT IS ABOUT, and that is not tidiness.
   *
   * The first version held a bare `AnchorOutcome` and reset it to `pending`
   * inside the effect. Effects run AFTER render, so for one render after the
   * step changed, the hook returned the PREVIOUS step's outcome — and the
   * consumer of this hook skips a step when it sees `absent`. Walk onto a step
   * whose predecessor was absent and it inherited that `absent` and skipped
   * itself, instantly, before it had been looked for. On a tour where two
   * absent steps sit together, the whole rest of the tour unwound in a single
   * frame.
   *
   * It is invisible in the ordinary case, because a step following a
   * successfully resolved one inherits `found` and nothing acts on that. It
   * only bites where a step is genuinely missing — which is the case this hook
   * exists for.
   *
   * Comparing the key during RENDER, rather than clearing it in an effect,
   * closes the window entirely: there is no frame in which a stale answer is
   * visible.
   */
  const [resolved, setResolved] = useState<{
    stepKey: string;
    outcome: AnchorOutcome;
  } | null>(null);

  useEffect(() => {
    if (!step) return;

    /*
     * A step with no anchor is centred, and that is decided during render
     * below rather than here — an effect would give it one frame of `pending`,
     * during which the card does not render at all and the opening step
     * flickers.
     */
    if (anchorsFor(step).length === 0) return;

    const setOutcome = (outcome: AnchorOutcome): void => {
      setResolved({ stepKey: step.key, outcome });
    };

    let cancelled = false;
    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;
    let found: HTMLElement | null = null;

    const remeasure = (): void => {
      if (cancelled || !found) return;
      // A resolved anchor can still be removed — a section's nav item
      // disappears when its query resolves to "no access". isConnected catches
      // that; a detached element measures as 0x0 at 0,0 and would spotlight the
      // top-left corner of the screen.
      if (!found.isConnected) {
        found = null;
        setOutcome({ state: 'absent' });
        return;
      }
      setOutcome({ state: 'found', rect: measure(found), element: found });
    };

    const attempt = (): boolean => {
      const el = findAnchorElement(step);
      if (!el) return false;
      found = el;
      el.scrollIntoView({ block: 'center', inline: 'nearest' });

      /*
       * MEASURE NOW, THEN AGAIN NEXT FRAME. Both, and the first one is not
       * redundant.
       *
       * The next-frame measure is the one that matters for accuracy:
       * `scrollIntoView` is not synchronous in every browser, so measuring in
       * the same tick can give the rect the element had BEFORE the scroll, and
       * the spotlight lands a few hundred pixels off.
       *
       * But `requestAnimationFrame` does not fire while the page is not
       * painting — a backgrounded tab, or a window behind another window. With
       * only the rAF measure, a step whose anchor was FOUND would sit in
       * `pending` for ever: the observer and the timeout are both torn down on
       * a successful find, so nothing was left to rescue it. The tour hung on a
       * dimmed screen with no card, permanently, and the only sign was the
       * database still saying `running`.
       *
       * Caught exactly that way on 2026-09-24 — the browser pane dropped behind
       * another window mid-step and the tour never came back.
       *
       * So: measure synchronously for a usable rect that is occasionally a
       * little stale, and refine it on the next frame whenever one arrives.
       */
      remeasure();
      requestAnimationFrame(() => {
        if (!cancelled) remeasure();
      });
      return true;
    };

    if (!attempt()) {
      observer = new MutationObserver(() => {
        if (cancelled) return;
        if (attempt()) {
          observer?.disconnect();
          observer = null;
          if (timeoutId !== null) window.clearTimeout(timeoutId);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        observer?.disconnect();
        observer = null;
        setOutcome({ state: 'absent' });
      }, ANCHOR_TIMEOUT_MS);
    }

    window.addEventListener('scroll', remeasure, true);
    window.addEventListener('resize', remeasure);

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      window.removeEventListener('scroll', remeasure, true);
      window.removeEventListener('resize', remeasure);
    };
  }, [step]);

  if (!step) return { state: 'pending' };
  if (anchorsFor(step).length === 0) return { state: 'centred' };
  /*
   * An answer about a different step is no answer about this one. `pending`
   * until this step has been looked for — which is what stops a skip
   * cascading, and what makes the outcome safe to act on.
   */
  if (resolved?.stepKey !== step.key) return { state: 'pending' };
  return resolved.outcome;
}
